import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  ASPECT_PRESETS,
  centeredAspectWindow,
  fetchMuxDuration,
  formatFrameTime,
  muxThumbnailUrl,
  parseAspectRatio,
  previewAspect,
  sampleFrameTimes,
} from "../../Utils/aspectPreview";

const AspectFrame = ({ playbackId, time, ratio }) => {
  const [frameRatio, setFrameRatio] = useState(null);
  const [failed, setFailed] = useState(false);
  const windowBox = frameRatio ? centeredAspectWindow(frameRatio, ratio) : null;

  return (
    <Box>
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          bgcolor: "#111",
          lineHeight: 0,
          minHeight: failed ? 120 : 0,
        }}
      >
        {failed ? (
          <Typography sx={{ color: "rgba(255,255,255,0.8)", p: 2 }} variant="body2">
            This frame could not be loaded.
          </Typography>
        ) : (
          <img
            src={muxThumbnailUrl(playbackId, time)}
            alt=""
            onLoad={(event) => {
              const image = event.currentTarget;
              if (image.naturalWidth && image.naturalHeight) {
                setFrameRatio(image.naturalWidth / image.naturalHeight);
              }
            }}
            onError={() => setFailed(true)}
            style={{ width: "100%", height: "auto", display: "block" }}
          />
        )}
        {windowBox && !failed && (
          <Box
            data-aspect-window=""
            style={{
              position: "absolute",
              left: `${windowBox.left}%`,
              top: `${windowBox.top}%`,
              width: `${windowBox.width}%`,
              height: `${windowBox.height}%`,
              boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.62)",
              outline: "1px solid rgba(255, 255, 255, 0.9)",
              pointerEvents: "none",
            }}
          />
        )}
      </Box>
      <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
        {formatFrameTime(time)}
      </Typography>
    </Box>
  );
};

const AspectPreviewDialog = ({
  open,
  onClose,
  playbackId,
  duration,
  onDuration,
  aspectPreset,
  aspectCustom,
  onPresetChange,
  onCustomChange,
}) => {
  const [times, setTimes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const durationRef = useRef(duration);
  const onDurationRef = useRef(onDuration);
  durationRef.current = duration;
  onDurationRef.current = onDuration;

  useEffect(() => {
    if (!open || !playbackId) return undefined;
    let cancelled = false;
    const load = async () => {
      setError("");
      try {
        let length = durationRef.current;
        if (!(length > 0)) {
          setLoading(true);
          length = await fetchMuxDuration(playbackId);
          if (cancelled) return;
          durationRef.current = length;
          onDurationRef.current(length);
        }
        if (!cancelled) setTimes(sampleFrameTimes(length));
      } catch (loadError) {
        if (!cancelled) {
          setTimes([]);
          setError(loadError.message || "Could not load frames.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, playbackId]);

  const aspectLabel = previewAspect(aspectPreset, aspectCustom);
  const ratio = parseAspectRatio(aspectLabel);
  const showingFallback = !aspectPreset;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Aspect preview</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "flex-start",
            flexDirection: { xs: "column", sm: "row" },
            mt: 1,
            mb: 2,
          }}
        >
          <FormControl fullWidth>
            <InputLabel id="aspect-preview-label">Aspect</InputLabel>
            <Select
              labelId="aspect-preview-label"
              label="Aspect"
              value={aspectPreset}
              onChange={(event) => onPresetChange(event.target.value)}
            >
              <MenuItem value="">Leave empty (16:9 fallback)</MenuItem>
              {ASPECT_PRESETS.filter(Boolean).map((preset) => (
                <MenuItem key={preset} value={preset}>
                  {preset === "custom" ? "Custom" : preset}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            disabled={loading || !(duration > 0 || times.length > 0)}
            onClick={() => {
              const length = durationRef.current;
              if (length > 0) setTimes(sampleFrameTimes(length));
            }}
            sx={{ height: 56, flexShrink: 0, whiteSpace: "nowrap" }}
          >
            New frames
          </Button>
        </Box>
        {aspectPreset === "custom" && (
          <TextField
            label="Custom aspect"
            value={aspectCustom}
            onChange={(event) => onCustomChange(event.target.value)}
            placeholder="2.41:1"
            fullWidth
            sx={{ mb: 2 }}
          />
        )}
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          {ratio
            ? showingFallback
              ? "Nothing is saved yet, so the clear window is the 16:9 fallback. The dimmed area sits outside that ratio."
              : `The clear window is ${aspectLabel}, centered on each frame. The dimmed area sits outside that ratio.`
            : "Enter a ratio like 2.41:1 to draw the window."}
        </Typography>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        {!loading && ratio && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            {times.map((time, index) => (
              <AspectFrame
                key={`${index}-${time}`}
                playbackId={playbackId}
                time={time}
                ratio={ratio}
              />
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AspectPreviewDialog;
